import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDownToLine,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock3,
  Copy,
  CreditCard,
  IndianRupee,
  LayoutDashboard,
  Link2,
  Loader2,
  Lock,
  LogOut,
  Menu,
  MessageCircle,
  Network,
  QrCode,
  Send,
  ShieldCheck,
  Smartphone,
  Upload,
  UserRound,
  Users,
  WalletCards,
  X,
} from "lucide-react";

import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  addDoc,
  where,
} from "firebase/firestore";



import { onAuthStateChanged, signOut } from "firebase/auth";

import { Link, useNavigate } from "react-router-dom";

import { auth, db } from "../firebase/firebase";


/* =========================================================
   USER DASHBOARD
========================================================= */

function UserDashboard() {
  const navigate = useNavigate();

  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userData, setUserData] = useState(null);

  const [payment, setPayment] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskSubmissions, setTaskSubmissions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);

  const [authLoading, setAuthLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);

  const [activeSection, setActiveSection] = useState("overview");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [paymentFile, setPaymentFile] = useState(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  const [copySuccess, setCopySuccess] = useState(false);
  const [upiCopySuccess, setUpiCopySuccess] = useState(false);

  const [logoutLoading, setLogoutLoading] = useState(false);

  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawUpi, setWithdrawUpi] = useState("");
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");


  /* =========================================================
     AUTH LISTENER
  ========================================================= */

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setAuthLoading(false);

      if (!user) {
        navigate("/login", { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);


  /* =========================================================
     USER + FIREBASE DATA LISTENERS
  ========================================================= */

  useEffect(() => {
    if (!firebaseUser) return undefined;

    setDataLoading(true);

    const unsubscribers = [];

    /* ---------------------------------------------------------
       USER PROFILE
    --------------------------------------------------------- */

    const userRef = doc(db, "users", firebaseUser.uid);

    const unsubscribeUser = onSnapshot(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setUserData({
            id: snapshot.id,
            ...snapshot.data(),
          });
        } else {
          setUserData({
            uid: firebaseUser.uid,
            name: firebaseUser.displayName || "",
            displayName: firebaseUser.displayName || "",
            email: firebaseUser.email || "",
            role: "user",
            paymentStatus: "not_submitted",
            softwareActivated: false,
          });
        }

        setDataLoading(false);
      },
      (error) => {
        console.error("User profile listener error:", error);
        setActionError("Unable to load your account information.");
        setDataLoading(false);
      }
    );

    unsubscribers.push(unsubscribeUser);


    /* ---------------------------------------------------------
       PAYMENT
    --------------------------------------------------------- */

    const paymentQuery = query(
      collection(db, "payments"),
      where("uid", "==", firebaseUser.uid)
    );

    const unsubscribePayments = onSnapshot(
      paymentQuery,
      (snapshot) => {
        const paymentList = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        paymentList.sort(
          (a, b) =>
            getTimestampValue(b.submittedAt) -
            getTimestampValue(a.submittedAt)
        );

        setPayment(paymentList[0] || null);
      },
      (error) => {
        console.error("Payment listener error:", error);
      }
    );

    unsubscribers.push(unsubscribePayments);


    /* ---------------------------------------------------------
       WALLET
    --------------------------------------------------------- */

    const walletRef = doc(db, "wallets", firebaseUser.uid);

    const unsubscribeWallet = onSnapshot(
      walletRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setWallet(snapshot.data());
        } else {
          setWallet({
            availableBalance: 0,
            pendingBalance: 0,
            totalEarned: 0,
            totalWithdrawn: 0,
          });
        }
      },
      (error) => {
        console.error("Wallet listener error:", error);
      }
    );

    unsubscribers.push(unsubscribeWallet);


    /* ---------------------------------------------------------
       TASKS + TASK SUBMISSIONS
    --------------------------------------------------------- */

    const tasksQuery = query(collection(db, "tasks"));

    const unsubscribeTasks = onSnapshot(
      tasksQuery,
      (snapshot) => {
        const taskList = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((task) => {
            if (task.status === "inactive") return false;
            if (!task.assignedTo) return true;
            if (Array.isArray(task.assignedTo)) {
              return task.assignedTo.includes(firebaseUser.uid);
            }
            return task.assignedTo === firebaseUser.uid;
          });

        taskList.sort(
          (a, b) => getTimestampValue(b.createdAt) - getTimestampValue(a.createdAt)
        );
        setTasks(taskList);
      },
      (error) => {
        console.error("Tasks listener error:", error);
        setActionError("Unable to load assigned tasks.");
      }
    );

    unsubscribers.push(unsubscribeTasks);

    const submissionsQuery = query(
      collection(db, "taskSubmissions"),
      where("uid", "==", firebaseUser.uid)
    );

    const unsubscribeSubmissions = onSnapshot(
      submissionsQuery,
      (snapshot) => {
        const submissionList = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        submissionList.sort(
          (a, b) => getTimestampValue(b.submittedAt) - getTimestampValue(a.submittedAt)
        );
        setTaskSubmissions(submissionList);
      },
      (error) => {
        console.error("Task submissions listener error:", error);
        setActionError("Unable to load task submission status.");
      }
    );

    unsubscribers.push(unsubscribeSubmissions);

    /* ---------------------------------------------------------
       REFERRALS
    --------------------------------------------------------- */

    const referralsQuery = query(
      collection(db, "referrals"),
      where("referrerUid", "==", firebaseUser.uid)
    );

    const unsubscribeReferrals = onSnapshot(
      referralsQuery,
      async (snapshot) => {
        const referralList = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        setReferrals(referralList);
      },
      (error) => {
        console.error("Referral listener error:", error);
      }
    );

    unsubscribers.push(unsubscribeReferrals);


    /* ---------------------------------------------------------
       TRANSACTIONS
    --------------------------------------------------------- */

    const transactionsQuery = query(
      collection(db, "transactions"),
      where("uid", "==", firebaseUser.uid)
    );

    const unsubscribeTransactions = onSnapshot(
      transactionsQuery,
      (snapshot) => {
        const transactionList = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        transactionList.sort(
          (a, b) =>
            getTimestampValue(b.createdAt) -
            getTimestampValue(a.createdAt)
        );

        setTransactions(transactionList);
      },
      (error) => {
        console.error("Transactions listener error:", error);
      }
    );

    unsubscribers.push(unsubscribeTransactions);


    /* ---------------------------------------------------------
       WITHDRAWALS
    --------------------------------------------------------- */

    const withdrawalsQuery = query(
      collection(db, "withdrawals"),
      where("uid", "==", firebaseUser.uid)
    );

    const unsubscribeWithdrawals = onSnapshot(
      withdrawalsQuery,
      (snapshot) => {
        const withdrawalList = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        withdrawalList.sort(
          (a, b) =>
            getTimestampValue(b.createdAt) -
            getTimestampValue(a.createdAt)
        );

        setWithdrawals(withdrawalList);
      },
      (error) => {
        console.error("Withdrawal listener error:", error);
      }
    );

    unsubscribers.push(unsubscribeWithdrawals);


    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [firebaseUser]);


  /* =========================================================
     PAYMENT STATUS
  ========================================================= */

  const subscriptionExpiryDate = userData?.subscriptionExpiryDate || null;
  const subscriptionExpiryMs = subscriptionExpiryDate
    ? getTimestampValue(subscriptionExpiryDate)
    : 0;
  const hasSubscriptionExpiry = Boolean(subscriptionExpiryDate);
  const subscriptionExpired =
    hasSubscriptionExpiry && subscriptionExpiryMs > 0 && subscriptionExpiryMs <= Date.now();

  // Legacy approved users without an expiry remain active until their account
  // is migrated to the monthly subscription fields. New approvals receive an
  // explicit one-month expiry from the Admin Dashboard.
  const subscriptionActive =
    Boolean(userData?.softwareActivated) && !subscriptionExpired;

  const latestPaymentStatus =
    payment?.status ||
    userData?.paymentStatus ||
    (userData?.softwareActivated ? "approved" : "not_submitted");

  const isApproved = subscriptionActive;
  const isExpired = subscriptionExpired;
  const isPending = !subscriptionActive && latestPaymentStatus === "pending";
  const isRejected = !subscriptionActive && latestPaymentStatus === "rejected";


  /* =========================================================
     USER VALUES
  ========================================================= */

  const displayName =
    userData?.displayName ||
    userData?.name ||
    firebaseUser?.displayName ||
    firebaseUser?.email?.split("@")[0] ||
    "User";

  const referralCode =
    userData?.referralCode || "";

  const referralLink = useMemo(() => {
    if (!referralCode) return "";

    return `${window.location.origin}/signup?ref=${encodeURIComponent(
      referralCode
    )}`;
  }, [referralCode]);


  /* =========================================================
     WALLET VALUES
  ========================================================= */

  const availableBalance = Number(
    wallet?.availableBalance || 0
  );

  const pendingBalance = Number(
    wallet?.pendingBalance || 0
  );

  const totalEarned = Number(
    wallet?.totalEarned || 0
  );

  const totalWithdrawn = Number(
    wallet?.totalWithdrawn || 0
  );


  /* =========================================================
     TASK VALUES
  ========================================================= */

  const completedTasks = tasks.filter((task) => {
    const submission = getTaskSubmission(taskSubmissions, task.id);
    return (
      submission?.status === "approved" ||
      submission?.status === "completed" ||
      task?.status === "approved" ||
      task?.status === "completed"
    );
  }).length;


  /* =========================================================
     REFERRAL VALUES
  ========================================================= */

  const referralCount = referrals.length;

  const referralEarnings = referrals.reduce((sum, referral) => {
    const isApprovedReferral =
      referral?.status === "approved" ||
      referral?.commissionStatus === "credited" ||
      Number(referral?.commission || 0) > 0;

    return isApprovedReferral
      ? sum + Number(referral?.commission || 0)
      : sum;
  }, 0);

  const paidWithdrawalTotal = withdrawals.reduce((sum, item) => {
    return item?.status === "paid"
      ? sum + Number(item?.amount || 0)
      : sum;
  }, 0);

  const pendingWithdrawalTotal = withdrawals.reduce((sum, item) => {
    return item?.status === "pending" || item?.status === "processing"
      ? sum + Number(item?.amount || 0)
      : sum;
  }, 0);

  // Admin deducts paid withdrawals from wallet. Pending requests are reserved
  // here in the UI so the same balance cannot be requested twice while waiting.
  const withdrawableBalance = Math.max(
    0,
    availableBalance - pendingWithdrawalTotal
  );

  const referralVerifiedPurchases = referrals.filter((referral) => {
    return (
      referral?.status === "approved" ||
      referral?.commissionStatus === "credited" ||
      Number(referral?.commission || 0) > 0
    );
  }).length;


  /* =========================================================
     PAYMENT LABEL
  ========================================================= */

  const paymentStatusLabel = useMemo(() => {
    if (isApproved) return "Subscription Active";
    if (isExpired) return "Subscription Expired";
    if (isPending) return "Payment Under Review";
    if (isRejected) return "Payment Rejected";

    return "Subscription Required";
  }, [isApproved, isExpired, isPending, isRejected]);


  /* =========================================================
     CLEAR MESSAGES
  ========================================================= */

  useEffect(() => {
    if (!actionMessage && !actionError) return undefined;

    const timer = setTimeout(() => {
      setActionMessage("");
      setActionError("");
    }, 4000);

    return () => clearTimeout(timer);
  }, [actionMessage, actionError]);


  /* =========================================================
     MOBILE SIDEBAR
  ========================================================= */

  useEffect(() => {
    if (mobileSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileSidebarOpen]);


  const closeMobileSidebar = () => {
    setMobileSidebarOpen(false);
  };


  /* =========================================================
     CHANGE SECTION
  ========================================================= */

  const changeSection = (section) => {
    setActiveSection(section);
    closeMobileSidebar();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };


  /* =========================================================
     PAYMENT FILE
  ========================================================= */

  const handlePaymentFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setActionError("");
    setActionMessage("");

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      setActionError(
        "Please upload a JPG, PNG or WEBP image."
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setActionError(
        "Payment screenshot must be smaller than 5 MB."
      );
      return;
    }

    setPaymentFile(file);
  };


  /* =========================================================
     PAYMENT SUBMIT
  ========================================================= */

const handlePaymentSubmit = async () => {
    if (!firebaseUser) {
      setActionError("Please login again.");
      return;
    }

    if (!paymentFile) {
      setActionError("Please select your payment screenshot.");
      return;
    }

    if (paymentSubmitting) return;

    try {
      setPaymentSubmitting(true);
      setActionError("");
      setActionMessage("");

      const screenshotUrl = await compressImageToDataUrl(paymentFile);

      if (!screenshotUrl) {
        throw new Error("Unable to process the screenshot.");
      }

      if (screenshotUrl.length > 900000) {
        throw new Error(
          "This screenshot is too large after compression. Please choose a smaller image."
        );
      }

      await addDoc(collection(db, "payments"), {
        uid: firebaseUser.uid,
        amount: 1000,
        screenshotUrl,
        status: "pending",
        submittedAt: serverTimestamp(),
      });

      setPaymentFile(null);
      setActionMessage(
        "Payment screenshot submitted successfully. It is now pending admin verification."
      );
    } catch (error) {
      console.error("Payment submission error:", error);
      setActionError(
        getFirebaseErrorMessage(
          error,
          "Unable to submit payment screenshot."
        )
      );
    } finally {
      setPaymentSubmitting(false);
    }
  };

  /* =========================================================
     COPY REFERRAL
  ========================================================= */

  const handleCopyReferral = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(
        referralLink
      );

      setCopySuccess(true);

      setTimeout(() => {
        setCopySuccess(false);
      }, 1800);
    } catch (error) {
      console.error("Copy referral error:", error);

      setActionError(
        "Unable to copy referral link."
      );
    }
  };


  /* =========================================================
     COPY PAYMENT UPI
  ========================================================= */

  const handleCopyPaymentUpi = async () => {
    const paymentUpi = "Q905790106@ybl";

    try {
      await navigator.clipboard.writeText(paymentUpi);
      setUpiCopySuccess(true);

      setTimeout(() => {
        setUpiCopySuccess(false);
      }, 1800);
    } catch (error) {
      console.error("Copy payment UPI error:", error);
      setActionError("Unable to copy UPI ID.");
    }
  };


  /* =========================================================
     SHARE REFERRAL ON WHATSAPP
  ========================================================= */

  const handleShareReferralWhatsApp = () => {
    if (!referralLink) {
      setActionError("Referral link is not available yet.");
      return;
    }

    const message = [
    "Hey! 👋 Join me on Refrex.",
    "",
    "Get access to WhatsApp Automation software and start completing tasks.",
    "",
    "Register here:",
    referralLink,
  ].join("\n");

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };


  /* =========================================================
     LOGOUT
  ========================================================= */

  const handleLogout = async () => {
    if (logoutLoading) return;

    try {
      setLogoutLoading(true);

      await signOut(auth);

      navigate("/login", {
        replace: true,
      });
    } catch (error) {
      console.error("Logout error:", error);

      setActionError(
        "Unable to logout. Please try again."
      );

      setLogoutLoading(false);
    }
  };


  /* =========================================================
     WITHDRAW
  ========================================================= */

  const handleWithdraw = async () => {
    if (!firebaseUser) {
      setActionError("Please login again.");
      return;
    }

    const amount = Number(withdrawAmount);

    if (!isApproved) {
      setActionError(
        "Your software payment must be approved before withdrawal."
      );
      return;
    }

    if (!amount || amount < 500) {
      setActionError(
        "Minimum withdrawal amount is ₹500."
      );
      return;
    }

    if (amount > withdrawableBalance) {
      setActionError(
        "Withdrawal amount cannot exceed your withdrawable balance after pending requests."
      );
      return;
    }

    const normalizedUpi = withdrawUpi.trim();
    if (!normalizedUpi) {
      setActionError("Please enter your UPI ID.");
      return;
    }

    if (!/^[a-zA-Z0-9._-]{2,}@[a-zA-Z]{2,}$/.test(normalizedUpi)) {
      setActionError("Please enter a valid UPI ID, for example name@upi.");
      return;
    }

    if (withdrawSubmitting) return;

    try {
      setWithdrawSubmitting(true);
      setActionError("");
      setActionMessage("");

      await addDoc(
        collection(db, "withdrawals"),
        {
          uid: firebaseUser.uid,
          amount,
          paymentMethod: {
            type: "upi",
            upiId: withdrawUpi.trim(),
          },
          status: "pending",
          createdAt: serverTimestamp(),
        }
      );

      setWithdrawAmount("");
      setWithdrawUpi("");

      setActionMessage(
        "Withdrawal request submitted successfully."
      );
    } catch (error) {
      console.error(
        "Withdrawal submission error:",
        error
      );

      setActionError(
        getFirebaseErrorMessage(
          error,
          "Unable to submit withdrawal request."
        )
      );
    } finally {
      setWithdrawSubmitting(false);
    }
  };


  /* =========================================================
     SIDEBAR
  ========================================================= */

  const sidebarItems = [
    {
      id: "overview",
      label: "Dashboard",
      icon: LayoutDashboard,
    },
    {
      id: "software",
      label: "My Software",
      icon: Smartphone,
    },
    {
      id: "tasks",
      label: "My Tasks",
      icon: ClipboardList,
      locked: !isApproved,
    },
    {
      id: "referrals",
      label: "My Referrals",
      icon: Users,
      locked: !isApproved,
    },
    {
      id: "team",
      label: "My Team",
      icon: Network,
      locked: !isApproved,
    },
    {
      id: "withdraw",
      label: "Withdraw",
      icon: WalletCards,
      locked: !isApproved,
    },
    {
      id: "transactions",
      label: "Transactions",
      icon: BarChart3,
    },
  ];


  /* =========================================================
     AUTH LOADING
  ========================================================= */

  if (authLoading) {
    return <DashboardLoader text="Checking your account..." />;
  }


  /* =========================================================
     NO USER
  ========================================================= */

  if (!firebaseUser) {
    return null;
  }


  /* =========================================================
     MAIN
  ========================================================= */

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#f5f7fa] text-[#08111f]">

      {/* =====================================================
          MOBILE OVERLAY
      ===================================================== */}

      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-[90] bg-slate-950/40 backdrop-blur-sm lg:hidden"
          onClick={closeMobileSidebar}
        />
      )}


      {/* =====================================================
          SIDEBAR
      ===================================================== */}

      <aside
        className={`fixed bottom-0 left-0 top-0 z-[100] w-[min(260px,88vw)] border-r border-slate-200 bg-white transition-transform duration-300 lg:translate-x-0 ${
          mobileSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full"
        }`}
      >

        {/* Brand */}

        <div className="flex h-[76px] items-center border-b border-slate-100 px-5">

          <Link
            to="/"
            onClick={closeMobileSidebar}
            className="flex items-center gap-3"
          >

            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#00a96d] text-base font-black text-white shadow-lg shadow-emerald-100">
              R
            </div>

            <div>
              <div className="text-[17px] font-black tracking-[-0.04em]">
                Refer<span className="text-[#00a96d]">x</span>
              </div>

              <div className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.16em] text-slate-400">
                User Dashboard
              </div>
            </div>

          </Link>

          <button
            type="button"
            onClick={closeMobileSidebar}
            className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500 lg:hidden"
          >
            <X className="h-4 w-4" />
          </button>

        </div>


        {/* User Profile */}

        <div className="border-b border-slate-100 p-4">

          <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">

            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <UserRound className="h-4 w-4" />
            </div>

            <div className="min-w-0">

              <div className="truncate text-xs font-black text-slate-800">
                {displayName}
              </div>

              <div className="mt-0.5 truncate text-[8px] text-slate-400">
                {firebaseUser.email}
              </div>

            </div>

          </div>

        </div>


        {/* Navigation */}

        <div className="px-3 py-5">

          <div className="mb-2 px-3 text-[8px] font-black uppercase tracking-[0.16em] text-slate-400">
            Account
          </div>

          <nav className="space-y-1">

            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const active =
                activeSection === item.id;

              return (
                <button
                  key={item.id}
                  type="button"
                  disabled={item.locked}
                  onClick={() =>
                    !item.locked &&
                    changeSection(item.id)
                  }
                  className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all duration-200 ${
                    active
                      ? "bg-emerald-50 text-[#00a96d]"
                      : item.locked
                        ? "cursor-not-allowed text-slate-300"
                        : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >

                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                      active
                        ? "bg-emerald-100 text-emerald-600"
                        : item.locked
                          ? "bg-slate-50"
                          : "bg-slate-100 text-slate-400 group-hover:bg-white group-hover:text-emerald-500"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>

                  <span className="flex-1 text-[11px] font-bold">
                    {item.label}
                  </span>

                  {item.locked && (
                    <Lock className="h-3 w-3 text-slate-300" />
                  )}

                  {active && !item.locked && (
                    <ChevronRight className="h-3.5 w-3.5 text-emerald-500" />
                  )}

                </button>
              );
            })}

          </nav>

        </div>


        {/* Logout */}

        <div className="absolute bottom-[92px] left-0 right-0 px-4">

          <button
            type="button"
            onClick={handleLogout}
            disabled={logoutLoading}
            className="flex w-full items-center gap-3 rounded-xl border border-slate-200 px-3 py-3 text-left text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
          >

            {logoutLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="h-4 w-4" />
            )}

            <span className="text-[11px] font-bold">
              {logoutLoading
                ? "Signing out..."
                : "Logout"}
            </span>

          </button>

        </div>


        {/* Status */}

        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-100 p-4">

          <div
            className={`rounded-xl border p-3 ${
              isApproved
                ? "border-emerald-200 bg-emerald-50"
                : isExpired
                  ? "border-rose-200 bg-rose-50"
                  : "border-amber-200 bg-amber-50"
            }`}
          >

            <div className="flex items-center gap-2">

              {isApproved ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              ) : (
                <Clock3 className="h-3.5 w-3.5 text-amber-600" />
              )}

              <span
                className={`text-[9px] font-black ${
                  isApproved
                    ? "text-emerald-700"
                    : "text-amber-700"
                }`}
              >
                {paymentStatusLabel}
              </span>

            </div>

            {!isApproved && (
              <p className="mt-1.5 text-[8px] leading-4 text-amber-600">
                Complete payment verification to unlock your account features.
              </p>
            )}

          </div>

        </div>

      </aside>


      {/* =====================================================
          MAIN AREA
      ===================================================== */}

      <div className="min-h-screen min-w-0 lg:pl-[260px]">

        {/* Header */}

        <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-xl">

          <div className="flex h-[76px] min-w-0 items-center justify-between px-4 sm:px-6 lg:px-8">

            <div className="flex items-center gap-3">

              <button
                type="button"
                onClick={() =>
                  setMobileSidebarOpen(true)
                }
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>

              <div>

                <div className="text-[10px] font-black uppercase tracking-[0.16em]">
  <span className="text-emerald-600">Refer</span>
  <span className="text-black">x</span>
  <span className="text-black"> Account</span>
</div>

                <h1 className="mt-0.5 text-lg font-black tracking-[-0.04em] text-[#08111f]">
                  {getSectionTitle(activeSection)}
                </h1>

              </div>

            </div>


            <div className="flex items-center gap-2">

              <Link
                to="/"
                className="hidden rounded-lg px-3 py-2 text-[10px] font-bold text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 sm:block"
              >
                Website
              </Link>

              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-100 bg-emerald-50 text-emerald-600">
                <UserRound className="h-4 w-4" />
              </div>

            </div>

          </div>

        </header>


        {/* Global Action Message */}

        {(actionMessage || actionError) && (
          <div className="px-5 pt-5 sm:px-6 lg:px-8">

            <div
              className={`mx-auto max-w-[1450px] rounded-xl border px-4 py-3 text-xs font-bold ${
                actionError
                  ? "border-rose-200 bg-rose-50 text-rose-600"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700"
              }`}
            >
              {actionError || actionMessage}
            </div>

          </div>
        )}


        {/* Content */}

        <div className="mx-auto w-full max-w-[1450px] min-w-0 px-4 py-5 sm:px-6 lg:px-8 lg:py-8">

          {dataLoading ? (
            <DashboardLoader text="Loading your dashboard..." />
          ) : (
            <>
              {activeSection === "overview" && (
                <OverviewSection
                  user={userData}
                  displayName={displayName}
                  paymentStatus={isExpired ? "expired" : latestPaymentStatus}
                  paymentStatusLabel={paymentStatusLabel}
                  subscriptionExpiryDate={subscriptionExpiryDate}
                  referralCode={referralCode}
                  referralLink={referralLink}
                  availableBalance={availableBalance}
                  withdrawableBalance={withdrawableBalance}
                  pendingWithdrawalTotal={pendingWithdrawalTotal}
                  pendingBalance={pendingBalance}
                  totalEarned={totalEarned}
                  totalWithdrawn={totalWithdrawn}
                  referralEarnings={referralEarnings}
                  referralCount={referralCount}
                  completedTasks={completedTasks}
                  referrals={referrals}
                  tasks={tasks}
                  transactions={transactions}
                  isApproved={isApproved}
                  onPayment={() =>
                    changeSection("software")
                  }
                  onReferrals={() =>
                    changeSection("referrals")
                  }
                  onTasks={() =>
                    changeSection("tasks")
                  }
                  onWithdraw={() =>
                    changeSection("withdraw")
                  }
                  onTeam={() => changeSection("team")}
                  onTransactions={() => changeSection("transactions")}
                  onSupport={() => navigate("/contact")}
                  onCopy={handleCopyReferral}
                  copySuccess={copySuccess}
                />
              )}


              {activeSection === "software" && (
                <SoftwareSection
                  paymentStatus={isExpired ? "expired" : latestPaymentStatus}
                  paymentStatusLabel={paymentStatusLabel}
                  subscriptionExpiryDate={subscriptionExpiryDate}
                  payment={payment}
                  paymentFile={paymentFile}
                  paymentSubmitting={paymentSubmitting}
                  isApproved={isApproved}
                  isExpired={isExpired}
                  isPending={isPending}
                  isRejected={isRejected}
                  onFileChange={handlePaymentFileChange}
                  onSubmit={handlePaymentSubmit}
                  onCopyPaymentUpi={handleCopyPaymentUpi}
                  upiCopySuccess={upiCopySuccess}
                  onTasks={() =>
                    changeSection("tasks")
                  }
                  onReferrals={() =>
                    changeSection("referrals")
                  }
                />
              )}


              {activeSection === "tasks" && (
                <TasksSection
                  tasks={tasks}
                  taskSubmissions={taskSubmissions}
                  isApproved={isApproved}
                  completedTasks={completedTasks}
                  onSoftware={() =>
                    changeSection("software")
                  }
                  firebaseUser={firebaseUser}
                  onActionMessage={setActionMessage}
                  onActionError={setActionError}
                />
              )}


              {activeSection === "referrals" && (
                <ReferralsSection
                  referrals={referrals}
                  isApproved={isApproved}
                  referralCode={referralCode}
                  referralLink={referralLink}
                  totalEarned={totalEarned}
                  referralEarnings={referralEarnings}
                  referralVerifiedPurchases={referralVerifiedPurchases}
                  onCopy={handleCopyReferral}
                  onShareWhatsApp={handleShareReferralWhatsApp}
                  copySuccess={copySuccess}
                  onSoftware={() =>
                    changeSection("software")
                  }
                />
              )}


              {activeSection === "team" && (
                <TeamSection
                  referrals={referrals}
                  isApproved={isApproved}
                />
              )}


              {activeSection === "transactions" && (
                <TransactionsPage
                  transactions={transactions}
                  withdrawals={withdrawals}
                  totalEarned={totalEarned}
                  totalWithdrawn={totalWithdrawn}
                  paidWithdrawalTotal={paidWithdrawalTotal}
                />
              )}


              {activeSection === "withdraw" && (
                <WithdrawSection
                  availableBalance={availableBalance}
                  withdrawableBalance={withdrawableBalance}
                  pendingWithdrawalTotal={pendingWithdrawalTotal}
                  pendingBalance={pendingBalance}
                  isApproved={isApproved}
                  amount={withdrawAmount}
                  upi={withdrawUpi}
                  submitting={withdrawSubmitting}
                  withdrawals={withdrawals}
                  onAmountChange={setWithdrawAmount}
                  onUpiChange={setWithdrawUpi}
                  onSubmit={handleWithdraw}
                  onSoftware={() =>
                    changeSection("software")
                  }
                />
              )}
            </>
          )}

        </div>

      </div>

    </main>
  );
}


/* =========================================================
   OVERVIEW
========================================================= */

function OverviewSection({
  user,
  displayName,
  paymentStatus,
  paymentStatusLabel,
  subscriptionExpiryDate,
  referralCode,
  referralLink,
  availableBalance,
  pendingBalance,
  totalEarned,
  totalWithdrawn,
  referralEarnings,
  referralCount,
  completedTasks,
  referrals,
  tasks,
  transactions,
  isApproved,
  onPayment,
  onReferrals,
  onTasks,
  onWithdraw,
  onTeam,
  onTransactions,
  onSupport,
  onCopy,
  copySuccess,
}) {
  return (
    <>
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">

          <div>

            <div className="flex flex-wrap items-center gap-2">

              <h2 className="text-xl font-black tracking-[-0.04em] sm:text-2xl">
                Welcome, {displayName}
              </h2>

              <PaymentBadge
                status={paymentStatus}
                label={paymentStatusLabel}
              />

            </div>

            <p className="mt-1.5 text-[10px] text-slate-400">
              {user?.createdAt
                ? `Member since ${formatDate(user.createdAt)}`
                : "Your Refrex account overview"}
            </p>

          </div>

          {referralCode && (
            <ReferralCodeBox
              referralCode={referralCode}
              referralLink={referralLink}
              onCopy={onCopy}
              copySuccess={copySuccess}
            />
          )}

        </div>

      </section>


      {/* Payment Alert */}

      {!isApproved && (
        <section className="mt-5 overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-sm">

          <div className="flex flex-col gap-4 p-5 sm:p-6 md:flex-row md:items-center md:justify-between">

            <div className="flex items-start gap-3">

              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                {paymentStatus === "pending" ? (
                  <Clock3 className="h-5 w-5" />
                ) : (
                  <CreditCard className="h-5 w-5" />
                )}
              </div>

              <div>

                <h3 className="text-sm font-black text-slate-800">
                  {paymentStatus === "expired"
                    ? "Your monthly subscription has expired"
                    : paymentStatus === "pending"
                      ? "Your payment is under admin review"
                      : paymentStatus === "rejected"
                        ? "Your payment was not approved"
                        : "Start your ₹1,000 monthly subscription"}
                </h3>

                <p className="mt-1 max-w-2xl text-[10px] leading-5 text-slate-500">
                  {paymentStatus === "expired"
                    ? "Your one-month access period has ended. Renew for ₹1,000 to unlock tasks, referrals and withdrawals again."
                    : paymentStatus === "pending"
                      ? "Your payment screenshot has been submitted. Tasks, referrals and withdrawals will remain locked until admin approval."
                      : "Scan the payment QR, complete the ₹1,000 monthly subscription payment and upload your payment screenshot for manual verification."}
                </p>

              </div>

            </div>

            {paymentStatus !== "pending" && (
              <button
                type="button"
                onClick={onPayment}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#00a96d] px-5 py-3 text-xs font-bold text-white shadow-lg shadow-emerald-100 transition hover:bg-[#00945f]"
              >
                Complete Payment
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}

          </div>

        </section>
      )}


      {/* Approved */}

      {isApproved && (
        <section className="mt-5 overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50">

          <div className="flex flex-col gap-4 p-5 sm:p-6 md:flex-row md:items-center md:justify-between">

            <div className="flex items-start gap-3">

              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white">
                <BadgeCheck className="h-5 w-5" />
              </div>

              <div>

                <h3 className="text-sm font-black text-emerald-900">
                  Software Access Activated
                </h3>

                <p className="mt-1 text-[10px] leading-5 text-emerald-700">
                  Your ₹1,000 monthly subscription has been verified. Your account features are now unlocked until the subscription expiry date.
                </p>

                {subscriptionExpiryDate && (
                  <p className="mt-2 text-[10px] font-black text-emerald-800">
                    Valid until: {formatDate(subscriptionExpiryDate)}
                  </p>
                )}

              </div>

            </div>

            <div className="flex flex-wrap gap-2">

              <button
                type="button"
                onClick={onTasks}
                className="rounded-lg bg-white px-4 py-2.5 text-[10px] font-bold text-emerald-700 shadow-sm"
              >
                View Tasks
              </button>

              <button
                type="button"
                onClick={onReferrals}
                className="rounded-lg bg-[#00a96d] px-4 py-2.5 text-[10px] font-bold text-white"
              >
                Start Referring
              </button>

            </div>

          </div>

        </section>
      )}


      {/* Stats */}

      <section className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

        <DashboardStat
          label="Available Earnings"
          value={formatCurrency(availableBalance)}
          icon={WalletCards}
          iconClass="bg-emerald-100 text-emerald-600"
          note="Available for withdrawal"
        />

        <DashboardStat
          label="Pending Earnings"
          value={formatCurrency(pendingBalance)}
          icon={Clock3}
          iconClass="bg-blue-100 text-blue-600"
          note="Awaiting processing"
        />

        <DashboardStat
          label="Total Earnings"
          value={formatCurrency(totalEarned)}
          icon={IndianRupee}
          iconClass="bg-purple-100 text-purple-600"
          note="Approved lifetime earnings"
        />

        <DashboardStat
          label="Completed Tasks"
          value={completedTasks}
          icon={ClipboardList}
          iconClass="bg-indigo-100 text-indigo-600"
          note="Approved task submissions"
        />

        <DashboardStat
          label="Total Withdrawn"
          value={formatCurrency(totalWithdrawn)}
          icon={ArrowDownToLine}
          iconClass="bg-rose-100 text-rose-600"
          note="Paid withdrawal requests"
        />

      </section>


      {/* Quick Actions */}

      <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">

        <h2 className="text-sm font-black">
          Dashboard Quick Actions
        </h2>

        <p className="mt-1 text-[10px] text-slate-400">
          Manage your Refrex account from one place.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-7">

          <QuickDashboardAction
            icon={Smartphone}
            label="Software"
            onClick={onPayment}
            active
          />

          <QuickDashboardAction
            icon={ClipboardList}
            label="My Tasks"
            onClick={onTasks}
            locked={!isApproved}
          />

          <QuickDashboardAction
            icon={Users}
            label="Refer & Earn"
            onClick={onReferrals}
            locked={!isApproved}
          />

          <QuickDashboardAction
            icon={Network}
            label="My Team"
            onClick={onTeam}
            locked={!isApproved}
          />

          <QuickDashboardAction
            icon={BarChart3}
            label="Transactions"
            onClick={onTransactions}
          />

          <QuickDashboardAction
            icon={WalletCards}
            label="Withdraw"
            onClick={onWithdraw}
            locked={!isApproved}
          />

          <QuickDashboardAction
            icon={MessageCircle}
            label="Support"
            onClick={onSupport}
          />

        </div>

      </section>


      {/* Performance */}

      <section className="mt-5 grid gap-5 xl:grid-cols-[1.65fr_0.85fr]">

        <EarningsChart
          transactions={transactions}
        />

        <ReferralSummary
          referrals={referrals}
          referralCount={referralCount}
          totalEarned={referralEarnings}
          isApproved={isApproved}
        />

      </section>


      {/* Preview */}

      <section className="mt-5 grid gap-5 lg:grid-cols-2">

        <TasksPreview
          tasks={tasks}
          completedTasks={completedTasks}
          isApproved={isApproved}
        />

        <RecentReferrals
          referrals={referrals}
        />

      </section>


      <TransactionsTable
        transactions={transactions}
      />
    </>
  );
}


/* =========================================================
   SOFTWARE
========================================================= */

function SoftwareSection({
  paymentStatus,
  paymentStatusLabel,
  subscriptionExpiryDate,
  payment,
  paymentFile,
  paymentSubmitting,
  isApproved,
  isExpired,
  isPending,
  isRejected,
  onFileChange,
  onSubmit,
  onCopyPaymentUpi,
  upiCopySuccess,
  onTasks,
  onReferrals,
}) {
  if (isApproved) {
    return (
      <div>

        <PageHeading
          eyebrow="Software Access"
          title="WhatsApp Automation Software"
          description="Your software access has been verified and activated."
        />

        <section className="mt-6 rounded-2xl border border-emerald-200 bg-white p-6 shadow-sm">

          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">

            <div className="flex items-start gap-4">

              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                <BadgeCheck className="h-6 w-6" />
              </div>

              <div>

                <h2 className="text-base font-black">
                  Software Access Active
                </h2>

                <p className="mt-1.5 max-w-2xl text-xs leading-6 text-slate-500">
                  Your ₹1,000 monthly subscription has been manually verified by the admin. Your assigned tasks and referral system are available until the expiry date.
                </p>

                {subscriptionExpiryDate && (
                  <p className="mt-2 text-xs font-black text-emerald-700">
                    Valid until: {formatDate(subscriptionExpiryDate)}
                  </p>
                )}

              </div>

            </div>

            <div className="flex flex-wrap gap-2">

              <button
                type="button"
                onClick={onTasks}
                className="rounded-xl bg-[#00a96d] px-4 py-3 text-xs font-bold text-white"
              >
                My Tasks
              </button>

              <button
                type="button"
                onClick={onReferrals}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-bold text-slate-700"
              >
                Refer & Earn
              </button>

            </div>

          </div>

        </section>

      </div>
    );
  }


  return (
    <div>

      <PageHeading
        eyebrow="Software Activation"
        title={isExpired ? "Renew Your Refrex Subscription" : "Activate Your Refrex Account"}
        description={isExpired
          ? "Your previous one-month subscription has expired. Pay ₹1,000 again and submit your payment screenshot for manual verification."
          : "Complete the ₹1,000 monthly subscription payment and submit your payment screenshot for manual admin verification."}
      />

      <section className="mt-6 grid gap-5 lg:grid-cols-[0.8fr_1.2fr]">

        {/* QR */}

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

          <div className="text-center">

            <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">
              Monthly Subscription
            </div>

            <div className="mt-2 text-3xl font-black tracking-[-0.05em]">
              ₹1,000
            </div>

            <div className="mt-1 text-[10px] text-slate-400">
              WhatsApp Automation Software
            </div>

          </div>

          <div className="mx-auto mt-5 flex aspect-square w-full max-w-[280px] items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">

            <img
              src="/payment-qr.jpeg"
              alt="Refrex ₹1000 payment QR code"
              className="h-full w-full object-contain"
              onError={(event) => {
                event.currentTarget.style.display = "none";

                const fallback =
                  event.currentTarget.nextElementSibling;

                if (fallback) {
                  fallback.style.display = "flex";
                }
              }}
            />

            <div className="hidden h-full w-full flex-col items-center justify-center text-center">

              <QrCode className="h-12 w-12 text-slate-300" />

              <div className="mt-3 text-xs font-bold text-slate-500">
                Payment QR
              </div>

              <div className="mt-1 max-w-[180px] text-[9px] leading-5 text-slate-400">
                Add your actual QR image at:
                <br />
                <span className="font-bold">
                  /public/payment-qr.jpeg
                </span>
              </div>

            </div>

          </div>

          <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50/60 p-4 text-center">

            <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">
              UPI ID
            </div>

            <div className="mt-1 break-all text-sm font-black tracking-tight text-slate-800">
              Q905790106@ybl
            </div>

            <div className="mt-1 text-[9px] text-slate-500">
              You can also pay directly using this UPI ID.
            </div>

            <button
              type="button"
              onClick={onCopyPaymentUpi}
              className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-[10px] font-black text-emerald-700 shadow-sm ring-1 ring-emerald-200 transition hover:bg-emerald-50 active:scale-[0.98]"
            >
              {upiCopySuccess ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  Copy UPI ID
                </>
              )}
            </button>

          </div>

          <div className="mt-5 rounded-xl bg-slate-50 p-4">

            <div className="flex items-start gap-2.5">

              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />

              <p className="text-[9px] leading-5 text-slate-500">
                Pay exactly ₹1,000 for the monthly subscription using the displayed QR code. Keep your successful payment screenshot ready for verification.
              </p>

            </div>

          </div>

        </div>


        {/* Verification */}

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

          <div className="flex items-center gap-3">

            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
              <Upload className="h-5 w-5" />
            </div>

            <div>

              <h2 className="text-base font-black">
                Payment Verification
              </h2>

              <p className="mt-1 text-[10px] text-slate-400">
                Upload proof after completing the payment.
              </p>

            </div>

          </div>


          {/* Steps */}

          <div className="mt-6 grid gap-3 sm:grid-cols-3">

            <VerificationStep
              number="01"
              title="Pay"
              description="Complete ₹1,000 monthly payment."
              active
            />

            <VerificationStep
              number="02"
              title="Upload"
              description="Submit screenshot proof."
              active={Boolean(paymentFile)}
            />

            <VerificationStep
              number="03"
              title="Admin Review"
              description="Manual payment verification."
              active={isPending}
            />

          </div>


          {/* Status */}

          <div className="mt-5">

            <PaymentBadge
              status={paymentStatus}
              label={paymentStatusLabel}
              large
            />

          </div>


          {/* Existing payment */}

          {payment?.screenshotUrl && (
            <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-3">

              <div className="text-[8px] font-black uppercase tracking-wider text-slate-400">
                Latest Submitted Proof
              </div>

              <a
                href={payment.screenshotUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-2 text-[10px] font-bold text-emerald-600 hover:underline"
              >
                View Payment Screenshot
                <ArrowRight className="h-3 w-3" />
              </a>

            </div>
          )}


          {/* Upload */}

          {!isPending && (
            <>
              <label className="mt-5 block cursor-pointer">

                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={onFileChange}
                  className="hidden"
                />

                <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-7 text-center transition hover:border-emerald-300 hover:bg-emerald-50/30">

                  {paymentFile ? (
                    <>
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                        <Check className="h-5 w-5" />
                      </div>

                      <div className="mt-3 break-all text-xs font-bold text-slate-700">
                        {paymentFile.name}
                      </div>

                      <div className="mt-1 text-[9px] text-slate-400">
                        Screenshot selected successfully
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm">
                        <Upload className="h-5 w-5" />
                      </div>

                      <div className="mt-3 text-xs font-bold text-slate-700">
                        Upload Payment Screenshot
                      </div>

                      <div className="mt-1 text-[9px] text-slate-400">
                        JPG, PNG or WEBP — max 5 MB
                      </div>
                    </>
                  )}

                </div>

              </label>


              <button
                type="button"
                onClick={onSubmit}
                disabled={
                  !paymentFile ||
                  paymentSubmitting
                }
                className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#00a96d] px-5 py-3 text-xs font-bold text-white transition hover:bg-[#00945f] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >

                {paymentSubmitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Processing Screenshot...
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5" />
                    Submit for Admin Verification
                  </>
                )}

              </button>

            </>
          )}


          {/* Pending */}

          {isPending && (
            <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5">

              <div className="flex items-start gap-3">

                <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />

                <div>

                  <h3 className="text-sm font-black text-amber-900">
                    Payment Under Review
                  </h3>

                  <p className="mt-1.5 text-[10px] leading-5 text-amber-700">
                    Your payment screenshot has been submitted. An admin will manually verify the payment. Your tasks, referrals and withdrawal features will unlock after approval.
                  </p>

                </div>

              </div>

            </div>
          )}


          {/* Rejected */}

          {isRejected && (
            <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-5">

              <div className="flex items-start gap-3">

                <X className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />

                <div>

                  <h3 className="text-sm font-black text-red-900">
                    Payment Not Approved
                  </h3>

                  <p className="mt-1.5 text-[10px] leading-5 text-red-700">
                    Your previous payment proof was not approved. Please submit valid payment proof again.
                  </p>

                </div>

              </div>

            </div>
          )}

        </div>

      </section>

    </div>
  );
}


/* =========================================================
   TASKS
========================================================= */

function TasksSection({
  tasks,
  taskSubmissions,
  isApproved,
  completedTasks,
  onSoftware,
  firebaseUser,
  onActionMessage,
  onActionError,
}) {
  if (!isApproved) {
    return (
      <div>

        <PageHeading
          eyebrow="Assigned Work"
          title="My Assigned Tasks"
          description="Complete the tasks assigned to your account and submit the required proof for review."
        />

        <LockedFeature
          title="Tasks are Locked"
          description="Your ₹1,000 software payment must be manually verified by the admin before assigned tasks become available."
          buttonText="Go to Payment"
          onClick={onSoftware}
        />

      </div>
    );
  }


  if (tasks.length === 0) {
    return (
      <div>

        <PageHeading
          eyebrow="Assigned Work"
          title="My Assigned Tasks"
          description="Complete the tasks assigned to your account and submit the required proof for review."
        />

        <EmptyState
          icon={ClipboardList}
          title="No tasks assigned yet"
          description="When a task is assigned to your account, it will appear here with its instructions, reward and submission status."
        />

      </div>
    );
  }


  return (
    <div>

      <PageHeading
        eyebrow="Assigned Work"
        title="My Assigned Tasks"
        description="Complete the tasks assigned to your account and submit the required proof for review."
      />


      <div className="mt-6 grid gap-4 sm:grid-cols-3">

        <DashboardStat
          label="Assigned Tasks"
          value={tasks.length}
          icon={ClipboardList}
          iconClass="bg-blue-100 text-blue-600"
          note="Live account tasks"
        />

        <DashboardStat
          label="Completed"
          value={completedTasks}
          icon={CheckCircle2}
          iconClass="bg-emerald-100 text-emerald-600"
          note="Approved tasks"
        />

        <DashboardStat
          label="Task Rewards"
          value={formatCurrency(
            tasks.reduce(
              (sum, task) =>
                sum +
                Number(
                  task?.payout ??
                  task?.reward ??
                  0
                ),
              0
            )
          )}
          icon={IndianRupee}
          iconClass="bg-purple-100 text-purple-600"
          note="Assigned task value"
        />

      </div>


      <div className="mt-5 grid gap-4 lg:grid-cols-2">

        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            submission={getTaskSubmission(taskSubmissions, task.id)}
            firebaseUser={firebaseUser}
            onActionMessage={onActionMessage}
            onActionError={onActionError}
          />
        ))}

      </div>

    </div>
  );
}


/* =========================================================
   TASK CARD
========================================================= */

function TaskCard({
  task,
  submission,
  firebaseUser,
  onActionMessage,
  onActionError,
}) {
  const [submitting, setSubmitting] =
    useState(false);

  // Task status (active/inactive) controls availability.
  // Submission status controls the user's own task progress.
  const status =
    submission?.status ||
    task?.submissionStatus ||
    "assigned";

  const payout =
    Number(
      task?.payout ??
      task?.reward ??
      0
    );

  const handleSubmitTask = async () => {
    if (!firebaseUser || submitting) return;

    if (submission?.status === "pending") {
      onActionError("This task is already pending admin review.");
      return;
    }

    if (submission?.status === "approved" || submission?.status === "completed") {
      onActionMessage("This task has already been approved.");
      return;
    }

    try {
      setSubmitting(true);

      await addDoc(
        collection(db, "taskSubmissions"),
        {
          taskId: task.id,
          uid: firebaseUser.uid,
          status: "pending",
          submittedAt: serverTimestamp(),
        }
      );

      onActionMessage(
        "Task submitted successfully for admin review."
      );
    } catch (error) {
      console.error(
        "Task submission error:",
        error
      );

      onActionError(
        getFirebaseErrorMessage(
          error,
          "Unable to submit this task."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };


  const statusClass =
    status === "approved" ||
    status === "completed"
      ? "bg-emerald-50 text-emerald-600"
      : status === "pending"
        ? "bg-amber-50 text-amber-600"
        : "bg-blue-50 text-blue-600";


  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">

      <div className="flex items-start justify-between gap-3">

        <div className="min-w-0">

          <div className="text-[8px] font-black uppercase tracking-[0.15em] text-emerald-600">
            Campaign Task
          </div>

          <h3 className="mt-1 text-sm font-black text-slate-800">
            {task?.campaignName ||
              task?.title ||
              "Assigned Task"}
          </h3>

        </div>

        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[8px] font-bold ${statusClass}`}
        >
          {status}
        </span>

      </div>


      <p className="mt-3 text-[10px] leading-5 text-slate-500">
        {task?.description ||
          "Follow the assigned task instructions and complete the required action."}
      </p>


      <div className="mt-4 grid grid-cols-2 gap-3">

        <div className="rounded-xl bg-slate-50 p-3">

          <div className="text-[8px] font-black uppercase tracking-wider text-slate-400">
            Payout
          </div>

          <div className="mt-1 text-base font-black text-emerald-600">
            {formatCurrency(payout)}
          </div>

        </div>


        <div className="rounded-xl bg-slate-50 p-3">

          <div className="text-[8px] font-black uppercase tracking-wider text-slate-400">
            Task Status
          </div>

          <div className="mt-1 text-xs font-black text-slate-700">
            {status}
          </div>

        </div>

      </div>


      <div className="mt-4 flex flex-col gap-2 sm:flex-row">

        {task?.url && (
          <a
            href={task.url}
            target="_blank"
            rel="noreferrer"
            className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-xs font-black text-slate-700 transition hover:border-emerald-200 hover:text-emerald-600"
          >
            START NOW
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
        )}

        <button
          type="button"
          onClick={handleSubmitTask}
          disabled={
            submitting ||
            status === "pending" ||
            status === "approved" ||
            status === "completed"
          }
          className={`flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl text-xs font-black text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
            status === "pending"
              ? "bg-amber-500"
              : status === "approved" || status === "completed"
                ? "bg-emerald-600"
                : "bg-[#00a96d] hover:bg-[#00945f]"
          }`}
        >
          {submitting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Submitting...
            </>
          ) : status === "pending" ? (
            <>
              <Clock3 className="h-3.5 w-3.5" />
              Pending Review
            </>
          ) : status === "approved" || status === "completed" ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5" />
              Task Approved
            </>
          ) : (
            <>
              <Send className="h-3.5 w-3.5" />
              Submit Task
            </>
          )}
        </button>

      </div>

    </div>
  );
}


/* =========================================================
   REFERRALS
========================================================= */

function ReferralsSection({
  referrals,
  isApproved,
  referralCode,
  referralLink,
  totalEarned,
  referralEarnings,
  referralVerifiedPurchases,
  onCopy,
  onShareWhatsApp,
  copySuccess,
  onSoftware,
}) {
  if (!isApproved) {
    return (
      <div>

        <PageHeading
          eyebrow="Referral Program"
          title="My Referrals"
          description="Refer eligible users and earn ₹200 for every verified software purchase."
        />

        <LockedFeature
          title="Referral System Locked"
          description="Your referral system becomes active after your ₹1,000 software payment is manually approved."
          buttonText="Complete Payment"
          onClick={onSoftware}
        />

      </div>
    );
  }



  return (
    <div>

      <PageHeading
        eyebrow="Referral Program"
        title="My Referrals"
        description="Your verified referral network and referral earnings."
      />


      {/* Referral Link */}

      <section className="mt-6 overflow-hidden rounded-2xl bg-[#111a2e] p-5 text-white shadow-xl sm:p-6">

        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">

          <div>

            <div className="flex items-center gap-2">

              <Link2 className="h-4 w-4 text-emerald-400" />

              <h2 className="text-base font-black">
                Your Unique Referral Link
              </h2>

            </div>

            <p className="mt-2 max-w-2xl text-[10px] leading-5 text-slate-400">
              Share this link with eligible users. When they register through your referral and complete the ₹1,000 software purchase, the verified referral commission becomes eligible.
            </p>

          </div>


          <div className="rounded-xl border border-white/10 bg-white/5 p-3">

            <div className="text-[8px] font-black uppercase tracking-wider text-slate-500">
              Referral Code
            </div>

            <div className="mt-1 text-sm font-black text-emerald-400">
              {referralCode || "Not available"}
            </div>

          </div>

        </div>


        <div className="mt-5 flex flex-col gap-2 sm:flex-row">

          <div className="min-w-0 flex-1 truncate rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-[9px] text-slate-300">
            {referralLink ||
              "Referral link will be generated from your account code."}
          </div>

          <button
            type="button"
            onClick={onCopy}
            disabled={!referralLink}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#00a96d] px-5 text-xs font-bold text-white disabled:opacity-40"
          >

            {copySuccess ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy Link
              </>
            )}

          </button>

          <button
            type="button"
            onClick={onShareWhatsApp}
            disabled={!referralLink}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#25D366] px-5 text-xs font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Share on WhatsApp
          </button>

        </div>

      </section>


      {/* Stats */}

      <section className="mt-5 grid gap-4 sm:grid-cols-3">

        <DashboardStat
          label="Total Referrals"
          value={referrals.length}
          icon={Users}
          iconClass="bg-blue-100 text-blue-600"
          note="Registered through your link"
        />

        <DashboardStat
          label="Verified Purchases"
          value={referralVerifiedPurchases}
          icon={BadgeCheck}
          iconClass="bg-emerald-100 text-emerald-600"
          note="Approved software purchases"
        />

        <DashboardStat
          label="Referral Earnings"
          value={formatCurrency(referralEarnings)}
          icon={IndianRupee}
          iconClass="bg-purple-100 text-purple-600"
          note="Verified wallet earnings"
        />

      </section>


      {/* Table */}

      <section className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

        <div className="border-b border-slate-100 px-5 py-4 sm:px-6">

          <h2 className="text-sm font-black">
            Direct Referral Members
          </h2>

          <p className="mt-1 text-[10px] text-slate-400">
            Live referral data from Firebase.
          </p>

        </div>


        {referrals.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No referrals yet"
            description="Your referrals will appear here when eligible users register using your referral link."
            compact
          />
        ) : (
          <div className="overflow-x-auto">

            <table className="w-full min-w-[700px]">

              <thead>

                <tr className="border-b border-slate-100 bg-slate-50">

                  <TableHead>
                    Member
                  </TableHead>

                  <TableHead>
                    Registered
                  </TableHead>

                  <TableHead>
                    Software
                  </TableHead>

                  <TableHead>
                    Commission
                  </TableHead>

                  <TableHead>
                    Status
                  </TableHead>

                </tr>

              </thead>


              <tbody>

                {referrals.map((referral) => {

                  const purchased =
                    referral?.status === "approved" ||
                    referral?.softwarePurchased === true ||
                    referral?.purchased === true ||
                    Number(referral?.commission || 0) > 0;

                  const commissionCredited =
                    referral?.status === "approved" ||
                    referral?.commissionStatus === "credited" ||
                    Number(referral?.commission || 0) > 0;

                  return (
                    <tr
                      key={referral.id}
                      className="border-b border-slate-100 last:border-0"
                    >

                      <TableCell>
                        {referral.referredName ||
                          referral.name ||
                          referral.displayName ||
                          referral.referredEmail ||
                          referral.email ||
                          "User"}
                      </TableCell>

                      <TableCell>
                        {formatDate(
                          referral.createdAt
                        )}
                      </TableCell>

                      <TableCell>

                        {purchased ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-1 text-[8px] font-bold text-emerald-600">
                            Purchased
                          </span>
                        ) : (
                          <span className="rounded-full bg-amber-50 px-2 py-1 text-[8px] font-bold text-amber-600">
                            Pending
                          </span>
                        )}

                      </TableCell>

                      <TableCell>

                        {commissionCredited ? (
                          <span className="font-bold text-emerald-600">
                            ₹200
                          </span>
                        ) : (
                          <span className="text-slate-400">
                            Pending
                          </span>
                        )}

                      </TableCell>

                      <TableCell>
                        <span className="text-[9px] font-bold text-slate-500">
                          {referral.status ||
                            referral.commissionStatus ||
                            "Registered"}
                        </span>
                      </TableCell>

                    </tr>
                  );
                })}

              </tbody>

            </table>

          </div>
        )}

      </section>

    </div>
  );
}


/* =========================================================
   TEAM
========================================================= */

function TeamSection({
  referrals,
  isApproved,
}) {
  return (
    <div>

      <PageHeading
        eyebrow="Network"
        title="My Team"
        description="View the referral network connected to your account."
      />

      {!isApproved ? (
        <LockedFeature
          title="Team Network Locked"
          description="Your team section becomes available after your software payment is approved."
        />
      ) : (
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

          <div className="flex items-center justify-between">

            <div>

              <h2 className="text-sm font-black">
                Team / Senior Network
              </h2>

              <p className="mt-1 text-[10px] text-slate-400">
                Your referral network from live account data.
              </p>

            </div>

            <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-[9px] font-bold text-emerald-600">
              {referrals.length} Members
            </span>

          </div>


          {referrals.length === 0 ? (
            <EmptyState
              icon={Network}
              title="No team members yet"
              description="Your network will appear here after eligible users register through your referral link."
              compact
            />
          ) : (
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">

              {referrals.map((member) => (
                <div
                  key={member.id}
                  className="rounded-xl border border-slate-100 bg-slate-50 p-4"
                >

                  <div className="flex items-center gap-3">

                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                      <UserRound className="h-4 w-4" />
                    </div>

                    <div className="min-w-0">

                      <div className="truncate text-xs font-bold text-slate-700">
                        {member.referredName ||
                          member.name ||
                          member.displayName ||
                          member.referredEmail ||
                          member.email ||
                          "User"}
                      </div>

                      <div className="mt-1 text-[8px] text-slate-400">
                        {member.status ||
                          "Registered"}
                      </div>

                    </div>

                  </div>

                </div>
              ))}

            </div>
          )}

        </section>
      )}

    </div>
  );
}


/* =========================================================
   WITHDRAW
========================================================= */

function WithdrawSection({
  availableBalance,
  withdrawableBalance,
  pendingWithdrawalTotal,
  pendingBalance,
  isApproved,
  amount,
  upi,
  submitting,
  withdrawals,
  onAmountChange,
  onUpiChange,
  onSubmit,
  onSoftware,
}) {
  if (!isApproved) {
    return (
      <div>

        <PageHeading
          eyebrow="Wallet"
          title="Withdraw Available Earnings"
          description="Withdraw approved earnings to your saved UPI payment method."
        />

        <LockedFeature
          title="Withdrawals are Locked"
          description="Withdrawals become available only after your ₹1,000 software payment is manually approved."
          buttonText="Complete Payment"
          onClick={onSoftware}
        />

      </div>
    );
  }


  return (
    <div>

      <PageHeading
        eyebrow="Wallet"
        title="Withdraw Available Earnings"
        description="Request payout from your approved available balance."
      />


      <section className="mt-6 grid gap-5 lg:grid-cols-[1fr_0.8fr]">

        {/* Form */}

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">

            <div className="text-[9px] font-black uppercase tracking-wider text-emerald-600">
              Available for Withdrawal
            </div>

            <div className="mt-2 text-3xl font-black tracking-[-0.05em] text-emerald-700">
              {formatCurrency(
                withdrawableBalance
              )}
            </div>

            <div className="mt-1 text-[9px] text-emerald-600">
              Minimum withdrawal: ₹500
            </div>

          </div>


          <div className="mt-5">

            <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
              Withdrawal Amount
            </label>

            <div className="relative mt-2">

              <IndianRupee className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

              <input
                type="number"
                min="500"
                value={amount}
                onChange={(event) =>
                  onAmountChange(
                    event.target.value
                  )
                }
                placeholder="Minimum ₹500"
                className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm font-bold outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
              />

            </div>

          </div>


          <div className="mt-4">

            <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
              UPI ID
            </label>

            <input
              type="text"
              value={upi}
              onChange={(event) =>
                onUpiChange(
                  event.target.value
                )
              }
              placeholder="example@upi"
              className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-xs outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
            />

          </div>


          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#00a96d] text-xs font-bold text-white transition hover:bg-[#00945f] disabled:cursor-not-allowed disabled:opacity-50"
          >

            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <ArrowDownToLine className="h-4 w-4" />
                Confirm Payout Request
              </>
            )}

          </button>

        </div>


        {/* Summary */}

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

          <h2 className="text-sm font-black">
            Wallet Summary
          </h2>

          <div className="mt-5 space-y-3">

            <WalletRow
              label="Available Balance"
              value={formatCurrency(
                availableBalance
              )}
              green
            />

            <WalletRow
              label="Pending Balance"
              value={formatCurrency(
                pendingBalance
              )}
            />

            <WalletRow
              label="Minimum Withdrawal"
              value="₹500"
            />

            <WalletRow
              label="Pending Withdrawal Requests"
              value={formatCurrency(pendingWithdrawalTotal)}
            />

          </div>


          <div className="mt-5 rounded-xl bg-slate-50 p-4">

            <div className="flex items-start gap-2.5">

              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />

              <p className="text-[9px] leading-5 text-slate-500">
                Withdrawal requests are submitted for admin processing. Use a valid UPI ID and wait for the request status to be updated.
              </p>

            </div>

          </div>


          {withdrawals.length > 0 && (
            <div className="mt-5">

              <div className="text-[9px] font-black uppercase tracking-wider text-slate-400">
                Recent Withdrawal Requests
              </div>

              <div className="mt-3 space-y-2">

                {withdrawals.slice(0, 5).map(
                  (withdrawal) => (
                    <div
                      key={withdrawal.id}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-3"
                    >

                      <div>

                        <div className="text-xs font-black text-slate-700">
                          {formatCurrency(
                            withdrawal.amount
                          )}
                        </div>

                        <div className="mt-0.5 text-[8px] text-slate-400">
                          {formatDate(
                            withdrawal.createdAt
                          )}
                        </div>

                      </div>

                      <WithdrawalStatus status={withdrawal.status || "pending"} />

                    </div>
                  )
                )}

              </div>

            </div>
          )}

        </div>

      </section>

    </div>
  );
}


/* =========================================================
   EARNINGS CHART
========================================================= */

function EarningsChart({
  transactions,
}) {
  const validTransactions =
    (transactions || [])
      .filter(
        (transaction) =>
          Number.isFinite(
            Number(transaction?.amount)
          )
      )
      .sort(
        (a, b) =>
          getTimestampValue(
            a.createdAt
          ) -
          getTimestampValue(
            b.createdAt
          )
      );


  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

      <div className="flex items-start justify-between gap-3">

        <div>

          <h2 className="text-sm font-black">
            Earnings & Performance Growth
          </h2>

          <p className="mt-1 text-[10px] text-slate-400">
            Cumulative performance based on verified account transactions.
          </p>

        </div>

        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[8px] font-bold text-emerald-600">
          Live Data
        </span>

      </div>


      <div className="mt-5 h-[240px] rounded-xl border border-slate-100 bg-slate-50/70">

        {validTransactions.length === 0 ? (
          <EmptyState
            icon={BarChart3}
            title="No earnings data yet"
            description="Your earnings graph will populate automatically from verified Firebase transactions."
            compact
          />
        ) : (
          <SimpleChart
            transactions={
              validTransactions
            }
          />
        )}

      </div>

    </section>
  );
}


/* =========================================================
   SIMPLE CHART
========================================================= */

function SimpleChart({
  transactions,
}) {
  const values =
    transactions.map(
      (transaction) =>
        Number(transaction.amount) || 0
    );

  const max =
    Math.max(...values, 1);

  const points =
    values
      .map((value, index) => {
        const x =
          values.length === 1
            ? 50
            : 5 +
              (index /
                (values.length - 1)) *
                90;

        const y =
          90 -
          (value / max) *
            70;

        return `${x},${y}`;
      })
      .join(" ");


  return (
    <div className="relative h-full w-full p-5">

      <div className="absolute left-5 right-5 top-5 border-t border-slate-200" />

      <div className="absolute left-5 right-5 top-1/2 border-t border-slate-200" />

      <div className="absolute bottom-8 left-5 right-5 border-t border-slate-200" />


      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-5 h-[calc(100%-48px)] w-[calc(100%-40px)]"
      >

        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-emerald-500"
        />

        {values.map(
          (value, index) => {

            const x =
              values.length === 1
                ? 50
                : 5 +
                  (index /
                    (values.length - 1)) *
                    90;

            const y =
              90 -
              (value / max) *
                70;

            return (
              <circle
                key={`${index}-${value}`}
                cx={x}
                cy={y}
                r="1.7"
                className="fill-emerald-500"
              />
            );
          }
        )}

      </svg>


      <div className="absolute bottom-3 left-5 text-[8px] text-slate-400">
        Start
      </div>

      <div className="absolute bottom-3 right-5 text-[8px] text-slate-400">
        Current
      </div>

    </div>
  );
}


/* =========================================================
   REFERRAL SUMMARY
========================================================= */

function ReferralSummary({
  referrals,
  referralCount,
  totalEarned,
  isApproved,
}) {
  const activeCount =
    referrals.filter(
      (referral) =>
        referral?.softwarePurchased === true ||
        referral?.purchased === true ||
        referral?.commissionStatus ===
          "credited"
    ).length;


  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

      <div className="flex items-start justify-between gap-3">

        <div>

          <h2 className="text-sm font-black">
            Referral Summary
          </h2>

          <p className="mt-1 text-[10px] text-slate-400">
            Your live referral performance.
          </p>

        </div>

        <Users className="h-4 w-4 text-purple-500" />

      </div>


      <div className="mt-5 grid grid-cols-2 gap-3">

        <SummaryBox
          label="Total Invited"
          value={referralCount}
        />

        <SummaryBox
          label="Paid / Active"
          value={activeCount}
          green
        />

      </div>


      <div className="mt-3 rounded-xl border border-purple-100 bg-purple-50 p-4">

        <div className="text-[8px] font-black uppercase tracking-wider text-purple-500">
          Referral Commissions
        </div>

        <div className="mt-2 flex items-end justify-between gap-3">

          <span className="text-[9px] text-purple-600">
            Verified referral earnings
          </span>

          <span className="text-lg font-black text-purple-600">
            {formatCurrency(
              totalEarned
            )}
          </span>

        </div>

      </div>


      {!isApproved && (
        <div className="mt-3 rounded-xl bg-slate-900 p-4 text-white">

          <div className="flex items-center gap-2 text-[9px] font-black">

            <Lock className="h-3.5 w-3.5 text-emerald-400" />

            Referral system locked

          </div>

          <p className="mt-1.5 text-[8px] leading-5 text-slate-400">
            Admin approval is required before referral earnings are activated.
          </p>

        </div>
      )}

    </section>
  );
}


/* =========================================================
   TASK PREVIEW
========================================================= */

function TasksPreview({
  tasks,
  completedTasks,
  isApproved,
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

      <div className="flex items-start justify-between gap-3">

        <div>

          <h2 className="text-sm font-black">
            My Assigned Tasks
          </h2>

          <p className="mt-1 text-[10px] text-slate-400">
            Your latest assigned work.
          </p>

        </div>

        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[8px] font-bold text-slate-500">
          {completedTasks} completed
        </span>

      </div>


      {!isApproved ? (
        <div className="mt-5 rounded-xl bg-slate-50 p-5 text-center">

          <Lock className="mx-auto h-5 w-5 text-slate-300" />

          <div className="mt-2 text-xs font-bold text-slate-500">
            Tasks Locked
          </div>

          <p className="mt-1 text-[9px] leading-5 text-slate-400">
            Complete payment verification to unlock tasks.
          </p>

        </div>
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="No tasks assigned yet"
          description="Assigned tasks will appear here when they become available."
          compact
        />
      ) : (
        <div className="mt-4 space-y-2">

          {tasks.slice(0, 3).map(
            (task) => (
              <TaskPreviewItem
                key={task.id}
                task={task}
              />
            )
          )}

        </div>
      )}

    </section>
  );
}


function TaskPreviewItem({
  task,
}) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">

      <div className="flex items-start justify-between gap-3">

        <div className="min-w-0">

          <h3 className="truncate text-xs font-black text-slate-800">
            {task?.campaignName ||
              task?.title ||
              "Assigned Task"}
          </h3>

          <p className="mt-1 text-[9px] leading-5 text-slate-400">
            {task?.description ||
              "Task instructions available in your account."}
          </p>

        </div>

        <span className="shrink-0 rounded-full bg-blue-50 px-2 py-1 text-[8px] font-bold text-blue-600">
          {task?.status || "assigned"}
        </span>

      </div>

      <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3">

        <span className="text-[8px] text-slate-400">
          Task Reward
        </span>

        <span className="text-[10px] font-black text-emerald-600">
          {formatCurrency(
            task?.payout ??
            task?.reward ??
            0
          )}
        </span>

      </div>

    </div>
  );
}


/* =========================================================
   RECENT REFERRALS
========================================================= */

function RecentReferrals({
  referrals,
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">

      <div>

        <h2 className="text-sm font-black">
          Recent Referrals
        </h2>

        <p className="mt-1 text-[10px] text-slate-400">
          Latest users connected through your referral.
        </p>

      </div>


      {referrals.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No referrals yet"
          description="Referral activity will appear here after users join through your referral link."
          compact
        />
      ) : (
        <div className="mt-4 space-y-2">

          {referrals.slice(0, 4).map(
            (referral) => {

              const purchased =
                referral?.status === "approved" ||
                referral?.purchased === true ||
                referral?.softwarePurchased === true ||
                Number(referral?.commission || 0) > 0;

              return (
                <div
                  key={referral.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3"
                >

                  <div className="flex min-w-0 items-center gap-3">

                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                      <UserRound className="h-3.5 w-3.5" />
                    </div>

                    <div className="min-w-0">

                      <div className="truncate text-[10px] font-bold text-slate-700">
                        {referral.name ||
                          referral.displayName ||
                          referral.email ||
                          "User"}
                      </div>

                      <div className="mt-0.5 text-[8px] text-slate-400">
                        {formatDate(
                          referral.createdAt
                        )}
                      </div>

                    </div>

                  </div>

                  <span className="shrink-0 text-[8px] font-bold text-emerald-600">
                    {purchased
                      ? "₹200"
                      : "Pending"}
                  </span>

                </div>
              );
            }
          )}

        </div>
      )}

    </section>
  );
}


/* =========================================================
   TRANSACTIONS
========================================================= */

function TransactionsTable({
  transactions,
}) {
  return (
    <section className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

      <div className="border-b border-slate-100 px-5 py-4 sm:px-6">

        <h2 className="text-sm font-black">
          Recent Ledger Transactions
        </h2>

        <p className="mt-1 text-[10px] text-slate-400">
          Verified wallet transaction history.
        </p>

      </div>


      {transactions.length === 0 ? (
        <EmptyState
          icon={WalletCards}
          title="No transactions yet"
          description="Verified earnings and wallet transactions will appear here from Firebase data."
          compact
        />
      ) : (
        <div className="overflow-x-auto">

          <table className="w-full min-w-[720px]">

            <thead>

              <tr className="border-b border-slate-100 bg-slate-50">

                <TableHead>
                  Date
                </TableHead>

                <TableHead>
                  Transaction ID
                </TableHead>

                <TableHead>
                  Description
                </TableHead>

                <TableHead>
                  Category
                </TableHead>

                <TableHead>
                  Amount
                </TableHead>

                <TableHead>
                  Status
                </TableHead>

              </tr>

            </thead>


            <tbody>

              {transactions.slice(0, 10).map(
                (transaction) => (
                  <tr
                    key={transaction.id}
                    className="border-b border-slate-100 last:border-0"
                  >

                    <TableCell>
                      {formatDate(
                        transaction.createdAt
                      )}
                    </TableCell>

                    <TableCell>
                      {transaction.id || "—"}
                    </TableCell>

                    <TableCell>
                      {transaction.description ||
                        (transaction.type === "referral_commission"
                          ? "Referral commission"
                          : transaction.type === "task_reward"
                            ? "Task reward"
                            : transaction.type === "withdrawal"
                              ? "Withdrawal payout"
                              : "Transaction")}
                    </TableCell>

                    <TableCell>
                      {transaction.category ||
                        transaction.type ||
                        "Wallet"}
                    </TableCell>

                    <TableCell>
                      {Number(transaction.amount || 0) < 0 ? (
                        <span className="font-bold text-rose-600">
                          -{formatCurrency(Math.abs(Number(transaction.amount || 0)))}
                        </span>
                      ) : (
                        <span className="font-bold text-emerald-600">
                          +{formatCurrency(transaction.amount)}
                        </span>
                      )}
                    </TableCell>

                    <TableCell>
                      <span className="rounded-full bg-emerald-50 px-2 py-1 text-[8px] font-bold text-emerald-600">
                        {transaction.status ||
                          "Completed"}
                      </span>
                    </TableCell>

                  </tr>
                )
              )}

            </tbody>

          </table>

        </div>
      )}

    </section>
  );
}


/* =========================================================
   TRANSACTIONS PAGE
========================================================= */

function TransactionsPage({
  transactions,
  withdrawals,
  totalEarned,
  totalWithdrawn,
  paidWithdrawalTotal,
}) {
  return (
    <div>
      <PageHeading
        eyebrow="Wallet Ledger"
        title="Transactions & Payout History"
        description="Track approved earnings, referral commissions, task rewards and withdrawal activity."
      />

      <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DashboardStat
          label="Total Earned"
          value={formatCurrency(totalEarned)}
          icon={IndianRupee}
          iconClass="bg-purple-100 text-purple-600"
          note="Approved lifetime earnings"
        />
        <DashboardStat
          label="Transactions"
          value={transactions.length}
          icon={BarChart3}
          iconClass="bg-blue-100 text-blue-600"
          note="Wallet ledger entries"
        />
        <DashboardStat
          label="Total Withdrawn"
          value={formatCurrency(Math.max(totalWithdrawn, paidWithdrawalTotal))}
          icon={ArrowDownToLine}
          iconClass="bg-rose-100 text-rose-600"
          note="Paid withdrawals"
        />
        <DashboardStat
          label="Withdrawals"
          value={withdrawals.length}
          icon={WalletCards}
          iconClass="bg-emerald-100 text-emerald-600"
          note="All payout requests"
        />
      </section>

      <TransactionsTable transactions={transactions} />

      <section className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
          <h2 className="text-sm font-black">Withdrawal History</h2>
          <p className="mt-1 text-[10px] text-slate-400">Status of your payout requests.</p>
        </div>

        {withdrawals.length === 0 ? (
          <EmptyState
            icon={WalletCards}
            title="No withdrawal requests yet"
            description="Your payout requests will appear here after you submit one."
            compact
          />
        ) : (
          <div className="divide-y divide-slate-100">
            {withdrawals.slice(0, 20).map((withdrawal) => (
              <div key={withdrawal.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div>
                  <div className="text-xs font-black text-slate-800">{formatCurrency(withdrawal.amount)}</div>
                  <div className="mt-1 text-[9px] text-slate-400">{formatDate(withdrawal.createdAt)} · {withdrawal.paymentMethod?.upiId || "UPI"}</div>
                </div>
                <WithdrawalStatus status={withdrawal.status || "pending"} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function WithdrawalStatus({ status }) {
  const normalized = String(status || "pending").toLowerCase();
  const config = {
    paid: "bg-emerald-50 text-emerald-600",
    approved: "bg-emerald-50 text-emerald-600",
    pending: "bg-amber-50 text-amber-600",
    processing: "bg-blue-50 text-blue-600",
    rejected: "bg-rose-50 text-rose-600",
  };

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-[8px] font-black uppercase ${config[normalized] || "bg-slate-100 text-slate-500"}`}>
      {normalized}
    </span>
  );
}


/* =========================================================
   REUSABLE
========================================================= */

function PageHeading({
  eyebrow,
  title,
  description,
}) {
  return (
    <div>

      <div className="text-[9px] font-black uppercase tracking-[0.18em] text-emerald-600">
        {eyebrow}
      </div>

      <h2 className="mt-2 text-2xl font-black tracking-[-0.05em] text-[#08111f] sm:text-3xl">
        {title}
      </h2>

      <p className="mt-2 max-w-2xl text-xs leading-6 text-slate-500">
        {description}
      </p>

    </div>
  );
}


function DashboardStat({
  label,
  value,
  icon: Icon,
  iconClass,
  note,
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">

      <div className="flex items-start justify-between gap-3">

        <div className="min-w-0">

          <div className="text-[8px] font-black uppercase tracking-[0.12em] text-slate-400">
            {label}
          </div>

          <div className="mt-2 truncate text-xl font-black tracking-[-0.04em]">
            {value}
          </div>

        </div>

        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${iconClass}`}
        >
          <Icon className="h-4 w-4" />
        </div>

      </div>

      <div className="mt-3 text-[8px] text-slate-400">
        {note}
      </div>

    </div>
  );
}


function PaymentBadge({
  status,
  label,
  large = false,
}) {
  const config = {
    approved: {
      icon: CheckCircle2,
      classes:
        "border-emerald-200 bg-emerald-50 text-emerald-600",
    },

    pending: {
      icon: Clock3,
      classes:
        "border-amber-200 bg-amber-50 text-amber-600",
    },

    rejected: {
      icon: X,
      classes:
        "border-red-200 bg-red-50 text-red-600",
    },

    expired: {
      icon: Lock,
      classes:
        "border-rose-200 bg-rose-50 text-rose-600",
    },

    not_submitted: {
      icon: Lock,
      classes:
        "border-slate-200 bg-slate-50 text-slate-500",
    },
  };

  const item =
    config[status] ||
    config.not_submitted;

  const Icon = item.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-black ${
        large
          ? "px-3 py-2 text-[9px]"
          : "px-2 py-1 text-[8px]"
      } ${item.classes}`}
    >

      <Icon
        className={
          large
            ? "h-3.5 w-3.5"
            : "h-2.5 w-2.5"
        }
      />

      {label}

    </span>
  );
}


function ReferralCodeBox({
  referralCode,
  onCopy,
  copySuccess,
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2.5">

      <div className="px-2">

        <div className="text-[8px] font-black uppercase tracking-[0.15em] text-slate-400">
          Referral Code
        </div>

        <div className="mt-0.5 text-sm font-black text-[#00a96d]">
          {referralCode}
        </div>

      </div>

      <button
        type="button"
        onClick={onCopy}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[9px] font-bold text-slate-600 transition hover:border-emerald-200 hover:text-emerald-600"
      >

        {copySuccess ? (
          <Check className="h-3 w-3" />
        ) : (
          <Copy className="h-3 w-3" />
        )}

        {copySuccess
          ? "Copied"
          : "Copy Link"}

      </button>

    </div>
  );
}


function QuickDashboardAction({
  icon: Icon,
  label,
  onClick,
  locked = false,
  active = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={locked}
      className={`group relative flex min-h-[68px] flex-col items-center justify-center gap-1.5 rounded-xl border px-3 py-3 transition-all duration-300 ${
        locked
          ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300"
          : active
            ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:-translate-y-1"
            : "border-slate-200 bg-white text-slate-600 hover:-translate-y-1 hover:border-emerald-200 hover:text-emerald-600"
      }`}
    >

      <Icon className="h-4 w-4" />

      <span className="text-[9px] font-bold">
        {label}
      </span>

      {locked && (
        <Lock className="absolute right-2 top-2 h-3 w-3 text-slate-300" />
      )}

    </button>
  );
}


function LockedFeature({
  title,
  description,
  buttonText,
  onClick,
}) {
  return (
    <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">

      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
        <Lock className="h-6 w-6" />
      </div>

      <h3 className="mt-4 text-base font-black">
        {title}
      </h3>

      <p className="mx-auto mt-2 max-w-lg text-xs leading-6 text-slate-500">
        {description}
      </p>

      {buttonText && onClick && (
        <button
          type="button"
          onClick={onClick}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#00a96d] px-5 py-3 text-xs font-bold text-white"
        >
          {buttonText}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}

    </section>
  );
}


function EmptyState({
  icon: Icon,
  title,
  description,
  compact = false,
}) {
  return (
    <div
      className={`text-center ${
        compact
          ? "p-8"
          : "mt-6 rounded-2xl border border-slate-200 bg-white p-10 shadow-sm"
      }`}
    >

      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-300">
        <Icon className="h-5 w-5" />
      </div>

      <h3 className="mt-3 text-sm font-black text-slate-600">
        {title}
      </h3>

      <p className="mx-auto mt-1.5 max-w-md text-[10px] leading-5 text-slate-400">
        {description}
      </p>

    </div>
  );
}


function VerificationStep({
  number,
  title,
  description,
  active = false,
}) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        active
          ? "border-emerald-200 bg-emerald-50"
          : "border-slate-100 bg-slate-50"
      }`}
    >

      <div
        className={`flex h-7 w-7 items-center justify-center rounded-lg text-[8px] font-black ${
          active
            ? "bg-emerald-500 text-white"
            : "bg-white text-slate-400"
        }`}
      >
        {number}
      </div>

      <div className="mt-2 text-[10px] font-black text-slate-700">
        {title}
      </div>

      <div className="mt-1 text-[8px] leading-4 text-slate-400">
        {description}
      </div>

    </div>
  );
}


function SummaryBox({
  label,
  value,
  green = false,
}) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        green
          ? "border-emerald-200 bg-emerald-50"
          : "border-slate-100 bg-slate-50"
      }`}
    >

      <div className="text-[8px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </div>

      <div
        className={`mt-1 text-xl font-black ${
          green
            ? "text-emerald-600"
            : "text-slate-800"
        }`}
      >
        {value}
      </div>

    </div>
  );
}


function WalletRow({
  label,
  value,
  green = false,
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-3">

      <span className="text-[9px] text-slate-400">
        {label}
      </span>

      <span
        className={`text-xs font-black ${
          green
            ? "text-emerald-600"
            : "text-slate-700"
        }`}
      >
        {value}
      </span>

    </div>
  );
}


function TableHead({
  children,
}) {
  return (
    <th className="px-5 py-3 text-left text-[8px] font-black uppercase tracking-wider text-slate-400">
      {children}
    </th>
  );
}


function TableCell({
  children,
}) {
  return (
    <td className="px-5 py-3 text-[9px] text-slate-500">
      {children}
    </td>
  );
}


/* =========================================================
   LOADER
========================================================= */

function DashboardLoader({
  text,
}) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">

      <div className="text-center">

        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-[#00a96d] text-white shadow-lg shadow-emerald-100">

          <Loader2 className="h-5 w-5 animate-spin" />

        </div>

        <p className="mt-4 text-xs font-bold text-slate-500">
          {text}
        </p>

      </div>

    </div>
  );
}


/* =========================================================
   HELPERS
========================================================= */

function getTaskSubmission(submissions, taskId) {
  const list = Array.isArray(submissions) ? submissions : [];
  for (let index = 0; index < list.length; index += 1) {
    if (list[index]?.taskId === taskId) return list[index];
  }
  return null;
}

function getSectionTitle(section) {
  const titles = {
    overview: "Dashboard",
    software: "My Software",
    tasks: "My Tasks",
    referrals: "My Referrals",
    team: "My Team",
    withdraw: "Withdraw",
    transactions: "Transactions",
  };

  return titles[section] || "Dashboard";
}


function formatCurrency(value) {
  const amount =
    Number(value) || 0;

  return `₹${amount.toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;
}


function formatDate(value) {
  if (!value) return "—";

  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return "—";
    }

    return date.toLocaleDateString(
      "en-IN",
      {
        day: "numeric",
        month: "short",
        year: "numeric",
      }
    );
  } catch {
    return "—";
  }
}


function getTimestampValue(value) {
  if (!value) return 0;

  try {
    if (
      typeof value?.toMillis ===
      "function"
    ) {
      return value.toMillis();
    }

    if (
      typeof value?.toDate ===
      "function"
    ) {
      return value.toDate().getTime();
    }

    const timestamp =
      new Date(value).getTime();

    return Number.isNaN(timestamp)
      ? 0
      : timestamp;
  } catch {
    return 0;
  }
}


async function compressImageToDataUrl(file) {
  const dataUrl = await readFileAsDataUrl(file);

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const maxWidth = 1280;
      const scale = Math.min(1, maxWidth / image.width);
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Your browser could not process this image."));
        return;
      }
      context.drawImage(image, 0, 0, width, height);
      const output = canvas.toDataURL("image/jpeg", 0.72);
      resolve(output);
    };
    image.onerror = () => reject(new Error("Invalid image file."));
    image.src = dataUrl;
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read the image."));
    reader.readAsDataURL(file);
  });
}

function getFirebaseErrorMessage(
  error,
  fallback
) {
  const code =
    error?.code || "";

  const messages = {
    "permission-denied":
      "You do not have permission to perform this action.",
    "auth/requires-recent-login":
      "Please login again and retry.",
  };

  return (
    messages[code] ||
    error?.message ||
    fallback
  );
}


export default UserDashboard;